import { useId } from "react";
import { phaseProgressText, type ProductionPhase } from "./production-phases";

const PHASE_STATE = { current: "current", complete: "done", open: "open", locked: "locked" } as const;

/**
 * Where the maker is (#101): Intake › Storyboard › Release on top, the steps of the open phase below.
 * Working alone every reachable phase opens; when Iris leads only done and current ones do (#99).
 */
export function PhasePath({ phases, guided, navigate, progress }: {
  phases: ProductionPhase[];
  guided: boolean;
  navigate: (step: string) => void;
  /** The whole production's progress, shown as a thin line when Iris leads. */
  progress?: number;
}) {
  const reasonId = useId();
  const current = phases.find((phase) => phase.state === "current");
  const clickable = (phase: ProductionPhase) =>
    guided ? phase.state === "current" || phase.state === "complete" : phase.state !== "locked";
  return (
    <>
      <nav className="production-phases" aria-label="Production phases">
        {phases.map((phase, index) => (
          <span key={phase.id}>
            {index > 0 && <span className="production-path-sep" aria-hidden="true"> › </span>}
            {clickable(phase) ? (
              <button
                type="button"
                className={`production-phase ${phase.state}`}
                aria-label={`${phase.label} phase, ${PHASE_STATE[phase.state]}`}
                aria-current={phase.state === "current" ? "step" : undefined}
                onClick={() => navigate(phase.target)}
              >
                {phase.label}
                {phase.done && " ✓"}
              </button>
            ) : phase.state === "locked" && phase.reason ? (
              // Focusable, so a keyboard or screen reader reaches the reason, not only a mouse tooltip.
              <>
                <button
                  type="button"
                  className="production-phase later"
                  aria-label={`${phase.label} phase, locked`}
                  aria-disabled="true"
                  aria-describedby={`${reasonId}-${phase.id}`}
                  title={phase.reason}
                >
                  {phase.label}
                </button>
                <span id={`${reasonId}-${phase.id}`} className="production-phase-reason">{phase.reason}</span>
              </>
            ) : (
              <span className="production-phase later">{phase.label}</span>
            )}
          </span>
        ))}
      </nav>
      {guided && (
        <div className="production-progress-line">
          <small>{phaseProgressText(phases)}</small>
          {progress !== undefined && (
            <span>
              <i style={{ width: `${Math.max(4, Math.min(100, progress))}%` }} />
            </span>
          )}
        </div>
      )}
      {current && current.steps.length > 1 && (guided ? (
        <nav className="production-path" aria-label="Production steps">
          {current.steps.map((step, index) => (
            <span key={step.id}>
              {index > 0 && <span className="production-path-sep" aria-hidden="true"> · </span>}
              {step.state === "complete" || step.state === "current" || step.state === "blocked" ? (
                <button
                  type="button"
                  className={`production-path-step ${step.state === "complete" ? "done" : "current"}`}
                  aria-label={`${step.label}, ${step.state === "complete" ? "done" : step.state === "blocked" ? "current step, blocked" : "current step"}`}
                  aria-current={step.state === "complete" ? undefined : "step"}
                  onClick={() => navigate(step.id)}
                >
                  {step.label}
                </button>
              ) : (
                <span className="production-path-step later">{step.label}</span>
              )}
            </span>
          ))}
        </nav>
      ) : (
        <nav className="lyric-steps" aria-label="Production steps">
          {current.steps.map((step, index) => (
            <button
              key={step.id}
              type="button"
              className={`lyric-step ${step.state === "current" ? "active" : ""} ${step.state === "complete" ? "ready" : ""} ${step.state === "blocked" ? "blocked" : ""}`}
              data-lyric-step={step.id}
              aria-label={`${step.label} step, ${step.state}`}
              aria-current={step.state === "current" ? "step" : undefined}
              onClick={() => navigate(step.id)}
            >
              <span aria-hidden="true">
                {step.state === "complete" ? "✓" : step.state === "blocked" ? "!" : index + 1}
              </span>
              <b>{step.label}</b>
              <small>{step.hint}</small>
            </button>
          ))}
        </nav>
      ))}
    </>
  );
}
